<template>
    <div class="container admin-table-page">
        <!-- ==================== TÍTULO ==================== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- ==================== FILTROS ==================== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100" style="flex-wrap: wrap; gap: 8px;">
                <!-- FILTROS PRINCIPALES - OCULTOS EN VISTA DETALLE -->
                <div v-if="!showDetailView" class="filters-container d-flex align-items-center">
                    <b-form-select
                        id="validity-select"
                        v-model="selVigencia"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="item in listVigencia" :key="item.vigencia" :value="item.vigencia">
                            {{ item?.vigencia }}
                        </option>
                    </b-form-select>

                    <b-button
                        class="consult-btn ml-2"
                        @click="consultEliminaciones()"
                        :disabled="!selVigencia"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>

                <!-- ============================================ -->
                <!-- BOTONES ORIGEN / DESTINO + DIFERENCIA + NOMBRE COMPAÑÍA -->
                <!-- ============================================ -->
                <div v-if="showDetailView" class="detail-header d-flex align-items-center w-100">
                    <div class="detail-tabs-buttons d-flex align-items-center">
                        <b-button
                            :class="activeTab === 'origen' ? 'tab-btn-origin-dest tab-active' : 'tab-btn-origin-dest tab-inactive'"
                            @click="activeTab = 'origen'"
                            :disabled="!ajusteOrig || ajusteOrig.length === 0"
                        >
                            <font-awesome-icon :icon="['fas', 'arrow-right-circle']" class="mr-1" />
                            {{ labels.origin }}
                            <b-badge :variant="activeTab === 'origen' ? 'light' : 'secondary'" class="ml-1">
                                {{ ajusteOrig ? ajusteOrig.length : 0 }}
                            </b-badge>
                        </b-button>

                        <div class="tab-separator"></div>

                        <b-button
                            :class="activeTab === 'destino' ? 'tab-btn-origin-dest tab-active' : 'tab-btn-origin-dest tab-inactive'"
                            @click="activeTab = 'destino'"
                            :disabled="!ajusteDesti || ajusteDesti.length === 0"
                        >
                            <font-awesome-icon :icon="['fas', 'arrow-left-circle']" class="mr-1" />
                            {{ labels.destination }}
                            <b-badge :variant="activeTab === 'destino' ? 'light' : 'secondary'" class="ml-1">
                                {{ ajusteDesti ? ajusteDesti.length : 0 }}
                            </b-badge>
                        </b-button>

                        <div class="tab-separator"></div>

                        <div class="diferencia-header-container d-flex align-items-center">
                            <div v-if="diferenciaTotal !== null && !isNaN(diferenciaTotal)" class="diferencia-box">
                                <span class="diferencia-label">{{ labels.diferencia || 'Diferencia:' }}</span>
                                <span class="diferencia-value" :class="diferenciaTotal >= 0 ? 'positive' : 'negative'">
                                    {{ formatCurrency(diferenciaTotal) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="detail-company-name ml-auto">
                        <span v-if="activeTab === 'origen' && nombreCompaniaOrigen" class="company-name-text">
                            <font-awesome-icon :icon="['fas', 'building']" class="mr-1" />
                            {{ nombreCompaniaOrigen }}
                        </span>
                        <span v-else-if="activeTab === 'destino' && nombreCompaniaDestino" class="company-name-text">
                            <font-awesome-icon :icon="['fas', 'building']" class="mr-1" />
                            {{ nombreCompaniaDestino }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ==================== CONTENIDO PRINCIPAL ==================== -->
        <div class="admin-table-block mt-4">
            <!-- ESTADO INICIAL -->
            <div v-if="!isLoading && !showDetailView && eliminaciones.length === 0 && !consultado">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">{{ labels.selectFilters }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- SECCIÓN: TABLA PRINCIPAL DE ELIMINACIONES -->
            <div v-if="!isLoading && !showDetailView && eliminaciones.length > 0 && consultado">
                <CorporateDataTable
                    :items="eliminaciones"
                    :fields="allFields"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :toolbar-disabled="false"
                    class="eliminaciones-table-wrapper"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-left d-flex align-items-center" style="gap: 8px; flex-wrap: wrap;">
                            <div class="opcion-buttons-group">
                                <b-button
                                    :class="opcionSeleccionada === '0' ? 'opcion-btn-active' : 'opcion-btn-inactive'"
                                    @click="opcionSeleccionada = '0'"
                                    :disabled="isVigenciaBloqueada"
                                >
                                    {{ labels.generar }}
                                </b-button>
                                <b-button
                                    :class="opcionSeleccionada === '1' ? 'opcion-btn-active' : 'opcion-btn-inactive'"
                                    @click="opcionSeleccionada = '1'"
                                >
                                    {{ labels.consultar }}
                                </b-button>
                                <b-button
                                    :class="opcionSeleccionada === '2' ? 'opcion-btn-active' : 'opcion-btn-inactive'"
                                    @click="opcionSeleccionada = '2'"
                                    :disabled="isVigenciaBloqueada"
                                >
                                    {{ labels.modificar }}
                                </b-button>
                                <b-button
                                    :class="opcionSeleccionada === 'auto' ? 'opcion-btn-active' : 'opcion-btn-inactive'"
                                    @click="f_generarAuto()"
                                    :disabled="isVigenciaBloqueada || !selVigencia"
                                >
                                    {{ labels.generarAuto }}
                                </b-button>
                            </div>
                        </div>
                    </template>

                    <template #cell(secuencia)="data">
                        <div class="text-center"><strong>{{ data.item?.secuencia }}</strong></div>
                    </template>

                    <template #cell(tipo_ajuste)="data">
                        <div class="text-left">{{ tipoAjuste(data.item?.tipo_ajuste) }}</div>
                    </template>

                    <template #cell(origen_nombre)="data">
                        <div class="text-left">{{ data.item?.origen_nombre }}</div>
                    </template>

                    <template #cell(tipo_origen)="data">
                        <div class="text-left">{{ data.item?.tipo_origen }}</div>
                    </template>

                    <template #cell(destino_nombre)="data">
                        <div class="text-left">{{ data.item?.destino_nombre }}</div>
                    </template>

                    <template #cell(tipo_destino)="data">
                        <div class="text-left">{{ data.item?.tipo_destino }}</div>
                    </template>

                    <!-- COLUMNA GENERAR (solo visible en opción 0) -->
                    <template #cell(accionGenerar)="data">
                        <b-button
                            v-if="opcionSeleccionada === '0'"
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border: none; border-radius: 4px;"
                            class="table-action-btn"
                            @click.stop="generarEliminacion(data.item.secuencia)"
                            :disabled="isVigenciaBloqueada || isSecuenciaBloqueada(data.item.secuencia)"
                            :title="isSecuenciaBloqueada(data.item.secuencia) ? labels.sequenceLocked : labels.generate"
                        >
                            <font-awesome-icon :icon="['fas', 'check']" size="xs" style="color: white;" />
                        </b-button>
                    </template>

                    <!-- COLUMNA MODIFICAR (solo visible en opción 2) -->
                    <template #cell(accionModificar)="data">
                        <b-button
                            v-if="opcionSeleccionada === '2'"
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border: none; border-radius: 4px;"
                            class="table-action-btn"
                            @click.stop="verDetalleEliminacion(data.item)"
                            :disabled="isVigenciaBloqueada"
                            :title="labels.modificar"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" style="color: white;" />
                        </b-button>
                    </template>

                    <!-- COLUMNA VER (solo visible en opción 1) -->
                    <template #cell(accionVer)="data">
                        <b-button
                            v-if="opcionSeleccionada === '1'"
                            style="background-color: #1f4f83; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border: none; border-radius: 4px;"
                            class="table-action-btn"
                            @click.stop="verDetalleEliminacion(data.item)"
                            :title="labels.viewDetails"
                        >
                            <font-awesome-icon :icon="['fas', 'eye']" size="xs" style="color: white;" />
                        </b-button>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA VACÍA -->
            <div v-if="!isLoading && !showDetailView && eliminaciones.length === 0 && consultado" class="text-center py-5">
                <font-awesome-icon
                    :icon="['fas', 'file-alt']"
                    size="4x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">{{ labels.empty }}</p>
            </div>

            <!-- SECCIÓN: VISTA DE DETALLE -->
            <div v-if="!isLoading && showDetailView">
                <div v-if="activeTab === 'origen'" class="detail-content">
                    <div v-if="ajusteOrig && ajusteOrig.length > 0">
                        <EliminaOrigen
                            :ajustOrigen.sync="ajusteOrig"
                            :codReporte="codReportePrincipal"
                            :id_compania="idCompaniaOrigen"
                            :vigencia="selVigencia"
                            :nombreCompania="nombreCompaniaOrigen"
                            :nombreCompaniaDestino="nombreCompaniaDestino"
                            :readOnly="isVigenciaBloqueada || opcionSeleccionada === '1'"
                            :secuenciaBloqueada="isSecuenciaBloqueada(secuenciaSeleccionada)"
                            :infoOpcion="parseInt(opcionSeleccionada)"
                            :infoSecuencia="secuenciaSeleccionada"
                            :tableLabels="detailTableLabels"
                            :tableFilterDefs="detailFilterDefs"
                            :defaultPerPage="10"
                            :pageOptions="[10, 20, 50, 100]"
                            :showBackButton="true"
                            @update:ajustOrigen="handleOrigenUpdate"
                            @back="cerrarDetalle"
                            @ajusteGuardado="refrescarDatos"
                            @cierraDetalle="cerrarDetalle"
                            @diferenciaCalculada="actualizarDiferencia"
                        />
                    </div>
                    <div v-else class="text-center py-4">
                        <font-awesome-icon :icon="['fas', 'info-circle']" size="2x" class="text-muted" />
                        <p class="text-muted mt-2">{{ labels.noOriginData }}</p>
                        <b-button variant="primary" @click="cerrarDetalle">
                            {{ labels.volver }}
                        </b-button>
                    </div>
                </div>

                <div v-if="activeTab === 'destino'" class="detail-content">
                    <div v-if="ajusteDesti && ajusteDesti.length > 0">
                        <EliminaDestino
                            :ajustDestino.sync="ajusteDesti"
                            :codReporte="codReportePrincipal"
                            :id_compania="idCompaniaDestino"
                            :vigencia="selVigencia"
                            :nombreCompania="nombreCompaniaDestino"
                            :readOnly="isVigenciaBloqueada || opcionSeleccionada === '1'"
                            :secuenciaBloqueada="isSecuenciaBloqueada(secuenciaSeleccionada)"
                            :infoOpcion="parseInt(opcionSeleccionada)"
                            :infoSecuencia="secuenciaSeleccionada"
                            :tableLabels="detailTableLabels"
                            :tableFilterDefs="detailFilterDefs"
                            :defaultPerPage="10"
                            :pageOptions="[10, 20, 50, 100]"
                            :showBackButton="true"
                            @update:ajustDestino="handleDestinoUpdate"
                            @back="cerrarDetalle"
                            @ajusteGuardado="refrescarDatos"
                        />
                    </div>
                    <div v-else class="text-center py-4">
                        <font-awesome-icon :icon="['fas', 'info-circle']" size="2x" class="text-muted" />
                        <p class="text-muted mt-2">{{ labels.noDestinationData }}</p>
                        <b-button variant="primary" @click="cerrarDetalle">
                            {{ labels.volver }}
                        </b-button>
                    </div>
                </div>
            </div>

            <!-- SPINNER DE CARGA -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import PlanCuentas from '../../services/PlanCuentas'
import Balance from "@/services/BalanceNiif"
import AjustesConsol from "@/services/AjustesConsol"
import EliminaOrigen from './EliminaOrigen.vue'
import EliminaDestino from './EliminaDestino.vue'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "Eliminaciones",
    components: {
        EliminaOrigen,
        EliminaDestino,
        CorporateDataTable
    },
    data: () => ({
        selVigencia: "",
        opcionSeleccionada: "1",
        listVigencia: [],
        eliminaciones: [],
        ajusteOrig: [],
        ajusteDesti: [],
        showDetailView: false,
        activeTab: 'origen',
        isLoading: false,
        selectedRow: null,
        nombreCompaniaOrigen: '',
        nombreCompaniaDestino: '',
        idCompaniaOrigen: null,
        idCompaniaDestino: null,
        secuenciaSeleccionada: null,
        codReportePrincipal: null,
        consultado: false,
        diferenciaTotal: null,
    }),
    computed: {
        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },
        labels() {
            const es = this.$store.state._language == "es-ES"
            return {
                title: es ? "Eliminación de Saldos" : "Elimination of Account Items",
                selectValidity: es ? "Seleccione Vigencia" : "Select Validity",
                consultar: es ? "Consultar" : "Consult",
                generar: es ? "Generar" : "Generate",
                modificar: es ? "Modificar" : "Modify",
                generarAuto: es ? "Ajuste Automático" : "Auto Generate",
                secuencia: es ? "Secuencia" : "Sequence",
                tipo_ajuste: es ? "Tipo Ajuste" : "Adjustment Type",
                origen_nombre: es ? "Empresa Origen" : "Origin Company",
                tipo_origen: es ? "Tipo Origen" : "Origin Type",
                destino_nombre: es ? "Empresa Destino" : "Destination Company",
                tipo_destino: es ? "Tipo Destino" : "Destination Type",
                ver: es ? "Ver" : "View",
                generar_accion: es ? "Generar" : "Generate",
                viewDetails: es ? "Ver detalles de la eliminación" : "View elimination details",
                generate: es ? "Generar eliminación" : "Generate elimination",
                empty: es ? "No hay registros disponibles" : "No records available",
                loading: es ? "Cargando..." : "Loading...",
                noData: es ? "No hay datos disponibles" : "No data available",
                noMatches: es ? "No se encontraron resultados" : "No results found",
                searchPlaceholder: es ? "Buscar en la tabla..." : "Search in table...",
                rowsPerPage: es ? "Filas por página:" : "Rows per page:",
                noSort: es ? "Sin orden" : "No sorting",
                sortAsc: es ? "A-Z / Menor a mayor" : "A-Z / Low to high",
                sortDesc: es ? "Z-A / Mayor a menor" : "Z-A / High to low",
                columns: es ? "Columnas" : "Columns",
                showAllColumns: es ? "Mostrar todas" : "Show all",
                filters: es ? "Filtros" : "Filters",
                clearFilters: es ? "Limpiar filtros" : "Clear filters",
                close: es ? "Cerrar" : "Close",
                backToList: es ? "Volver a la lista" : "Back to list",
                origin: es ? "Origen" : "Origin",
                destination: es ? "Destino" : "Destination",
                noOriginData: es ? "No hay cuentas de origen registradas" : "No origin accounts registered",
                noDestinationData: es ? "No hay cuentas de destino registradas" : "No destination accounts registered",
                volver: es ? "Volver" : "Back",
                vigencia: es ? "Vigencia" : "Validity",
                reporte: es ? "Reporte" : "Report",
                codReporte: es ? "Reporte" : "Report",
                selectFilters: es ? "Seleccione la vigencia y consulte" : "Select validity and consult",
                diferencia: es ? "Diferencia:" : "Difference:",
                // NUEVO: mensaje para secuencias 500+
                sequenceLocked: es ? "Las secuencias 500+ no se pueden modificar" : "Sequences 500+ cannot be modified",
            }
        },
        allFields() {
            const baseFields = [
                { key: 'secuencia', label: this.labels.secuencia, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', minWidth: '100px' },
                { key: 'tipo_ajuste', label: this.labels.tipo_ajuste, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '180px' },
                { key: 'origen_nombre', label: this.labels.origen_nombre, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'tipo_origen', label: this.labels.tipo_origen, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '150px' },
                { key: 'destino_nombre', label: this.labels.destino_nombre, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'tipo_destino', label: this.labels.tipo_destino, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '150px' }
            ]

            if (this.opcionSeleccionada === '0') {
                baseFields.push({ key: 'accionGenerar', label: this.labels.generar_accion, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '80px' })
            } else if (this.opcionSeleccionada === '1') {
                baseFields.push({ key: 'accionVer', label: this.labels.ver, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '60px' })
            } else if (this.opcionSeleccionada === '2') {
                baseFields.push({ key: 'accionModificar', label: this.labels.modificar, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '80px' })
            }

            return baseFields
        },
        tableFilterDefs() {
            const es = this.$store.state._language == "es-ES"
            return [
                { key: 'secuencia', type: 'text', placeholder: es ? 'Filtrar secuencia...' : 'Filter sequence...' },
                { key: 'tipo_ajuste', type: 'text', placeholder: es ? 'Filtrar tipo ajuste...' : 'Filter adjustment type...' },
                { key: 'origen_nombre', type: 'text', placeholder: es ? 'Filtrar empresa origen...' : 'Filter origin company...' },
                { key: 'tipo_origen', type: 'text', placeholder: es ? 'Filtrar tipo origen...' : 'Filter origin type...' },
                { key: 'destino_nombre', type: 'text', placeholder: es ? 'Filtrar empresa destino...' : 'Filter destination company...' },
                { key: 'tipo_destino', type: 'text', placeholder: es ? 'Filtrar tipo destino...' : 'Filter destination type...' }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        detailTableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        detailFilterDefs() {
            const es = this.$store.state._language == "es-ES"
            return [
                { key: 'cuenta', type: 'text', placeholder: es ? 'Filtrar cuenta...' : 'Filter account...' },
                { key: 'descripcion', type: 'text', placeholder: es ? 'Filtrar descripción...' : 'Filter description...' },
                { key: 'valor', type: 'number', placeholder: es ? 'Filtrar valor...' : 'Filter value...' }
            ]
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        // ============================================================
        // NUEVO: Determina si una secuencia está bloqueada (>= 500)
        // ============================================================
        isSecuenciaBloqueada(secuencia) {
            const num = parseInt(secuencia, 10);
            return !isNaN(num) && num >= 500;
        },

        formatCurrency(value) {
            if (value === 0 || value === "0" || value === null || value === "" || value === undefined) return ""
            return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
        },

        tipoAjuste(tipoAjus) {
            const es = this.$store.state._language == "es-ES"
            if (tipoAjus == 1) {
                return es ? 'Interes no controlante' : 'Non-controlling interest'
            } else if (tipoAjus == 2) {
                return es ? 'Eliminación en inversión' : 'Investment elimination'
            } else if (tipoAjus == 3) {
                return es ? 'Eliminación saldo recíproco' : 'Reciprocal balance elimination'
            } else if (tipoAjus == 4) {
                return es ? 'Ajuste del Combinado' : 'Combined adjustment'
            }
            return es ? 'Tipo desconocido' : 'Unknown type'
        },

        async f_generarAuto() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" 
                    ? 'La vigencia seleccionada está deshabilitada para generar/modificar.' 
                    : 'The selected validity is disabled for generating/modifying.')
                return
            }

            if (!this.selVigencia) {
                this.$toast.error(this.$store.state._language == "es-ES" 
                    ? 'Debe seleccionar una vigencia' 
                    : 'Please select a validity')
                return
            }

            this.isLoading = true

            try {
                const response = await Balance.generarAuto(this.selVigencia)
                
                if (response.data && response.data.success) {
                    this.$toast.success(response.data.message || 
                        (this.$store.state._language == "es-ES" 
                            ? 'Ajustes generados automáticamente' 
                            : 'Adjustments generated automatically'))
                    
                    this.opcionSeleccionada = '1'
                    await this.consultEliminaciones()
                } else {
                    this.$toast.error(response.data?.message || 
                        (this.$store.state._language == "es-ES" 
                            ? 'Error al generar ajustes automáticos' 
                            : 'Error generating automatic adjustments'))
                }
            } catch (error) {
                console.error('Error en generar auto:', error)
                this.$toast.error(this.$store.state._language == "es-ES" 
                    ? 'Error al generar ajustes automáticos' 
                    : 'Error generating automatic adjustments')
            } finally {
                this.isLoading = false
            }
        },

        calcularDiferencia() {
            let totalOrigen = 0;
            let totalDestino = 0;
            
            if (this.ajusteOrig) {
                this.ajusteOrig.forEach(item => {
                    totalOrigen += parseFloat(item.ajuste_funcional1) || 0;
                });
            }
            
            if (this.ajusteDesti) {
                this.ajusteDesti.forEach(item => {
                    totalDestino += parseFloat(item.ajuste_funcional1) || 0;
                });
            }
            
            this.diferenciaTotal = -(totalOrigen + totalDestino);
            this.diferenciaTotal = Math.round(this.diferenciaTotal * 100) / 100;
        },

        actualizarDiferencia(valor) {
            this.diferenciaTotal = valor;
        },

        async getReportePrincipal() {
            try {
                const response = await AjustesConsol.getReportePrincipal()
                return response.data?.codigo
            } catch (error) {
                console.error('Error al obtener reporte principal:', error)
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar el reporte principal' : 'Error loading main report')
                return null
            }
        },

        async consultEliminaciones() {
            if (!this.selVigencia) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Debe seleccionar vigencia primero' : 'Please select a validity first')
                return
            }

            this.isLoading = true
            this.cerrarDetalle()
            this.consultado = true

            const codReportePrincipal = await this.getReportePrincipal()
            if (!codReportePrincipal) {
                this.isLoading = false
                return
            }

            this.codReportePrincipal = codReportePrincipal

            Balance.getEliminaciones(codReportePrincipal, this.selVigencia, parseInt(this.opcionSeleccionada))
                .then((result) => {
                    this.eliminaciones = result.data.eliminaciones[0] || []
                    if (this.eliminaciones.length === 0) {
                        this.$toast.info(this.$store.state._language == "es-ES" ? 'No hay eliminaciones para esta vigencia' : 'No eliminations for this validity')
                    }
                })
                .catch((error) => {
                    console.error('Error consultando eliminaciones:', error)
                    this.eliminaciones = []
                    this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al consultar las eliminaciones' : 'Error consulting eliminations')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        async generarEliminacion(secuencia) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia seleccionada está deshabilitada para generar.' : 'The selected validity is disabled for generating.')
                return
            }

            // NUEVO: bloqueo por secuencia >= 500
            if (this.isSecuenciaBloqueada(secuencia)) {
                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? `La secuencia ${secuencia} no se puede generar (secuencias 500+ están bloqueadas).`
                        : `Sequence ${secuencia} cannot be generated (sequences 500+ are locked).`
                )
                return
            }

            this.isLoading = true

            try {
                const codReportePrincipal = await this.getReportePrincipal()
                if (!codReportePrincipal) {
                    this.isLoading = false
                    return
                }

                await Balance.genereEliminaciones(codReportePrincipal, this.selVigencia, secuencia)
                this.$toast.success(this.$store.state._language == "es-ES" ? 'Eliminación generada exitosamente' : 'Elimination generated successfully')
                await this.consultEliminaciones()
            } catch (error) {
                console.error('Error generando eliminación:', error)
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al generar la eliminación' : 'Error generating elimination')
            } finally {
                this.isLoading = false
            }
        },

        async verDetalleEliminacion(item) {
            this.selectedRow = item.secuencia
            this.secuenciaSeleccionada = item.secuencia
            this.activeTab = 'origen'
            this.showDetailView = true
            this.diferenciaTotal = null

            const codReportePrincipal = await this.getReportePrincipal()
            if (!codReportePrincipal) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar el reporte principal' : 'Error loading main report')
                return
            }

            this.codReportePrincipal = codReportePrincipal
            this.idCompaniaOrigen = item.origen
            this.idCompaniaDestino = item.destino
            this.nombreCompaniaOrigen = item.origen_nombre || `Compañía ${item.origen}`
            this.nombreCompaniaDestino = item.destino_nombre || `Compañía ${item.destino}`

            await this.cargarDetalles(item.secuencia)
        },

        async cargarDetalles(secuencia) {
            this.isLoading = true

            try {
                const codReportePrincipal = await this.getReportePrincipal()
                if (!codReportePrincipal) {
                    this.isLoading = false
                    return
                }

                const resultOrigen = await Balance.getEliminaOrigen(
                    this.selVigencia,
                    secuencia,
                    codReportePrincipal
                )
                this.ajusteOrig = resultOrigen.data.listEliminaOrigen[0] || []

                const resultDestino = await Balance.getEliminaDestino(
                    this.selVigencia,
                    secuencia,
                    codReportePrincipal
                )
                this.ajusteDesti = resultDestino.data.listEliminaDestino[0] || []

                this.calcularDiferencia()

                if (this.activeTab === 'origen' && (!this.ajusteOrig || this.ajusteOrig.length === 0)) {
                    if (this.ajusteDesti && this.ajusteDesti.length > 0) {
                        this.activeTab = 'destino'
                    }
                } else if (this.activeTab === 'destino' && (!this.ajusteDesti || this.ajusteDesti.length === 0)) {
                    if (this.ajusteOrig && this.ajusteOrig.length > 0) {
                        this.activeTab = 'origen'
                    }
                }

            } catch (error) {
                console.error('Error cargando detalles:', error)
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar los detalles' : 'Error loading details')
                this.cerrarDetalle()
            } finally {
                this.isLoading = false
            }
        },

        handleOrigenUpdate(newData) {
            this.ajusteOrig = newData
            this.calcularDiferencia()
        },

        handleDestinoUpdate(newData) {
            this.ajusteDesti = newData
            this.calcularDiferencia()
        },

        cerrarDetalle() {
            this.showDetailView = false
            this.selectedRow = null
            this.secuenciaSeleccionada = null
            this.ajusteOrig = []
            this.ajusteDesti = []
            this.nombreCompaniaOrigen = ''
            this.nombreCompaniaDestino = ''
            this.idCompaniaOrigen = null
            this.idCompaniaDestino = null
            this.activeTab = 'origen'
            this.diferenciaTotal = null
        },

        async refrescarDatos() {
            if (!this.secuenciaSeleccionada) return

            // Recargar SOLO los detalles de origen/destino sin cerrar la vista
            try {
                this.isLoading = true

                const codReportePrincipal = await this.getReportePrincipal()
                if (!codReportePrincipal) {
                    this.isLoading = false
                    return
                }

                const resultOrigen = await Balance.getEliminaOrigen(
                    this.selVigencia,
                    this.secuenciaSeleccionada,
                    codReportePrincipal
                )
                this.ajusteOrig = resultOrigen.data.listEliminaOrigen[0] || []

                const resultDestino = await Balance.getEliminaDestino(
                    this.selVigencia,
                    this.secuenciaSeleccionada,
                    codReportePrincipal
                )
                this.ajusteDesti = resultDestino.data.listEliminaDestino[0] || []

                this.calcularDiferencia()
            } catch (error) {
                console.error('Error refrescando datos:', error)
            } finally {
                this.isLoading = false
            }
        }
    },
    watch: {
        selVigencia(val) {
            if (val && this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia seleccionada está deshabilitada para generar o modificar.' : 'The selected validity is disabled for generating or modifying.')
            }
            if (val == "") {
                this.eliminaciones = []
                this.cerrarDetalle()
                this.consultado = false
            }
        },
        opcionSeleccionada() {
            if (this.selVigencia && this.consultado) {
                this.consultEliminaciones()
            }
        }
    },
    mounted() {
        this.isLoading = true
        PlanCuentas.getVigenciaPlancuentas()
            .then((result) => {
                this.listVigencia = result.data.getVigencia || []
            })
            .catch((err) => {
                console.log(err)
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar las vigencias' : 'Error loading validities')
            })
            .finally(() => {
                this.isLoading = false
            })
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
/* ========== ESTILOS DE FILTROS PRINCIPALES ========== */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ========== TOOLBAR DE TABLA ========== */
.niif-toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

/* ========== BOTONES DE OPCIÓN ========== */
.opcion-buttons-group {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
}

.opcion-buttons-group .opcion-btn-active,
.opcion-buttons-group .opcion-btn-inactive {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    flex-shrink: 0;
    border: 1px solid #1f4f83;
    transition: all 0.2s;
}

.opcion-buttons-group .opcion-btn-active {
    background-color: #1f4f83 !important;
    border-color: #1f4f83 !important;
    color: white !important;
}

.opcion-buttons-group .opcion-btn-active:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.opcion-buttons-group .opcion-btn-active:focus:not(:disabled) {
    box-shadow: 0 0 0 0.2rem rgba(31, 79, 131, 0.5) !important;
}

.opcion-buttons-group .opcion-btn-inactive {
    background-color: transparent !important;
    border-color: #1f4f83 !important;
    color: #1f4f83 !important;
}

.opcion-buttons-group .opcion-btn-inactive:hover:not(:disabled) {
    background-color: #1f4f83 !important;
    color: white !important;
}

.opcion-buttons-group .opcion-btn-inactive:focus:not(:disabled) {
    box-shadow: 0 0 0 0.2rem rgba(31, 79, 131, 0.5) !important;
}

.opcion-buttons-group .opcion-btn-active:disabled,
.opcion-buttons-group .opcion-btn-inactive:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ========== HEADER DE DETALLE ========== */
.detail-header {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
}

.detail-tabs-buttons {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 0;
    flex-wrap: wrap;
}

.tab-separator {
    width: 8px;
    min-width: 8px;
    flex-shrink: 0;
}

.detail-company-name {
    flex-shrink: 0;
    margin-left: auto;
    padding: 4px 12px;
    background-color: #f8f9fa;
    border-radius: 7px;
    border: 1px solid #dee2e6;
}

.company-name-text {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    white-space: nowrap;
}

/* ========== ESTILOS PARA DIFERENCIA ========== */
.diferencia-header-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.diferencia-box {
    display: inline-flex;
    align-items: center;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 7px;
    padding: 4px 16px;
    min-height: 38px;
    white-space: nowrap;
}

.diferencia-label {
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    margin-right: 8px;
}

.diferencia-value {
    font-size: 15px;
    font-weight: 600;
}

.diferencia-value.positive {
    color: #28a745;
}

.diferencia-value.negative {
    color: #dc3545;
}

/* ========== BOTONES ORIGEN / DESTINO ========== */
.tab-btn-origin-dest {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    flex-shrink: 0;
    border: 1px solid #6c757d;
    transition: all 0.2s;
    background-color: transparent;
    color: #6c757d;
}

.tab-btn-origin-dest.tab-active {
    background-color: #1f4f83 !important;
    border-color: #1f4f83 !important;
    color: white !important;
}

.tab-btn-origin-dest.tab-active:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.tab-btn-origin-dest.tab-active:focus:not(:disabled) {
    box-shadow: 0 0 0 0.2rem rgba(31, 79, 131, 0.5) !important;
}

.tab-btn-origin-dest.tab-inactive {
    background-color: transparent !important;
    border-color: #6c757d !important;
    color: #6c757d !important;
}

.tab-btn-origin-dest.tab-inactive:hover:not(:disabled) {
    background-color: #6c757d !important;
    color: white !important;
}

.tab-btn-origin-dest.tab-inactive:focus:not(:disabled) {
    box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5) !important;
}

.tab-btn-origin-dest:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.tab-btn-origin-dest .badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
    border-radius: 10px;
}

.tab-btn-origin-dest.tab-active .badge {
    background-color: rgba(255, 255, 255, 0.25) !important;
    color: white !important;
}

.tab-btn-origin-dest.tab-inactive .badge {
    background-color: #6c757d !important;
    color: white !important;
}

/* ========== ESTILOS DE FLECHA Y TÍTULO ========== */
.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

/* ========== BOTONES DE ACCIÓN EN TABLA ========== */
.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ========== ESTILOS DE TABLA CON SCROLL HORIZONTAL ========== */
.eliminaciones-table-wrapper {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.eliminaciones-table-wrapper .tblEstandar.tblCorporate td {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 0 !important;
}

.eliminaciones-table-wrapper .tblEstandar.tblCorporate {
    min-width: 100% !important;
    width: max-content !important;
    table-layout: fixed !important;
}

.eliminaciones-table-wrapper .tblEstandar.tblCorporate thead th {
    white-space: nowrap !important;
}

/* ========== ESTILOS DE DETALLE ========== */
.detail-content {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    .niif-header .consult-btn,
    .tab-btn-origin-dest {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
    .opcion-buttons-group .opcion-btn-active,
    .opcion-buttons-group .opcion-btn-inactive {
        height: 28px;
        min-height: 28px;
        font-size: 12px;
        padding: 0 12px;
    }
    .diferencia-box {
        padding: 3px 14px;
        min-height: 36px;
    }
    .diferencia-label { font-size: 13px; }
    .diferencia-value { font-size: 14px; }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    .niif-header .consult-btn,
    .tab-btn-origin-dest {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
    .tab-btn-origin-dest {
        flex: 1;
    }
    .detail-header {
        flex-wrap: wrap;
    }
    .detail-company-name {
        margin-left: 0 !important;
        width: 100%;
        text-align: center;
    }
    .tab-separator {
        width: 6px;
        min-width: 6px;
    }
    .opcion-buttons-group {
        flex-wrap: wrap;
    }
    .opcion-buttons-group .opcion-btn-active,
    .opcion-buttons-group .opcion-btn-inactive {
        flex: 1;
        min-width: 80px;
        justify-content: center;
    }
    .niif-toolbar-left {
        flex-wrap: wrap;
    }
    .detail-tabs-buttons {
        width: 100%;
    }
    .diferencia-header-container {
        margin-top: 4px;
        width: 100%;
        justify-content: flex-start;
    }
    .tab-separator:last-child {
        display: none;
    }
    .diferencia-box {
        padding: 3px 12px;
        min-height: 34px;
    }
    .diferencia-label { font-size: 12px; }
    .diferencia-value { font-size: 13px; }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    .niif-header .filters-container {
        gap: 4px;
    }
    .niif-header .consult-btn,
    .tab-btn-origin-dest {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
        font-size: 13px;
        padding: 0 16px;
    }
    .tab-btn-origin-dest.ml-2 {
        margin-left: 0 !important;
    }
    .detail-tabs-buttons {
        width: 100%;
        flex-direction: column;
        align-items: stretch !important;
    }
    .tab-separator {
        display: none;
    }
    .tab-btn-origin-dest:first-child {
        margin-bottom: 4px;
    }
    .detail-company-name {
        margin-top: 8px;
        padding: 6px 12px;
    }
    .company-name-text {
        font-size: 13px;
    }
    .opcion-buttons-group {
        width: 100%;
    }
    .opcion-buttons-group .opcion-btn-active,
    .opcion-buttons-group .opcion-btn-inactive {
        font-size: 11px;
        padding: 0 8px;
        height: 28px;
        min-height: 28px;
        flex: 1;
    }
    .niif-toolbar-left {
        width: 100%;
        flex-wrap: wrap;
    }
    .diferencia-header-container {
        flex-wrap: wrap;
        gap: 4px;
        width: 100%;
    }
    .diferencia-box {
        padding: 2px 12px;
        min-height: 32px;
        width: 100%;
        justify-content: center;
    }
    .diferencia-label { font-size: 12px; }
    .diferencia-value { font-size: 13px; }
}
</style>